
import Vue from 'vue'
import api from '@root/src/api/api'
import SpinnerLoader from '@root/src/components/SpinnerLoader.vue'
import BaseInputOutlined from '@root/src/components/inputs/BaseInputOutlined.vue'
import Opz2Metrics from './Opz2Metrics.vue'
import { formatNumber } from './opz2'

/** Код бэкенда: yClients ответил 403 на список пользователей филиала. */
const YC_USERS_NO_ACCESS = 'YCLIENTS_USERS_NO_ACCESS'

export default Vue.extend({
    name: 'Opz2KpiSettingsTab',
    components: { SpinnerLoader, BaseInputOutlined, Opz2Metrics },
    props: {
        projectId: { type: String, required: true },
        active: { type: Boolean, default: false },
    },
    data: () => ({
        loading: false,
        saving: false,
        error: false,
        loaded: false,
        clearDialog: false,
        scheme: { name: '', appliesTo: '', description: '', actions: {} as Record<string, any> },
        actionsCatalog: [] as any[],
        accrueWhenOptions: [] as any[],
        serviceTypes: [] as string[],
        staff: [] as any[],
        dhUsers: [] as any[],
        crmUsers: [] as any[],
        crmUsersLoading: false,
        /** yClients не дал интеграции доступ к сотрудникам филиала (403) */
        crmUsersAccessDenied: false,
        adminMapping: {} as Record<string, string | null>,
        stornoCards: [
            {
                title: 'Отмена ОПЗ',
                text:
                    'Если визит по ОПЗ отменён, визитная часть KPI не начисляется. Если была авансовая премия за запись — начисление снимается.',
            },
            {
                title: 'Отмена кросс-продажи',
                text:
                    'Отмена кросс-продажи считается отдельно от отмен ОПЗ и не смешивается в одной метрике KPI.',
            },
            {
                title: 'Перенос',
                text:
                    'Если меняется только дата визита, начисление ожидает фактического статуса визита по ОПЗ.',
            },
        ],
    }),
    computed: {
        enabledActions(): any[] {
            return Object.values(this.scheme.actions ?? {}).filter((a: any) => a?.enabled)
        },
        summaryMetrics(): any[] {
            const rates = this.enabledActions.map((a: any) => Number(a.rate ?? 0))
            const opzCreated = this.scheme.actions?.opzCreated
            return [
                {
                    label: 'Действий',
                    value: formatNumber(this.actionsCatalog.length),
                    hint: 'можно включать отдельно',
                },
                {
                    label: 'Ставка ОПЗ',
                    value: `${formatNumber(opzCreated?.enabled ? opzCreated.rate : 0)} ₽`,
                    hint: 'за факт записи',
                    tone: 'wasabi',
                },
                {
                    label: 'Макс. бонус',
                    value: `${formatNumber(rates.length ? Math.max(...rates) : 0)} ₽`,
                    hint: 'самое дорогое действие',
                    tone: 'teal',
                },
                {
                    label: 'Снятие премии',
                    value: 'Да',
                    hint: 'при отмене визита',
                    tone: 'amber',
                },
            ]
        },
    },
    watch: {
        active: {
            immediate: true,
            handler(isActive: boolean) {
                if (isActive && !this.loaded && !this.loading) {
                    this.load()
                }
            },
        },
    },
    methods: {
        /** Уже выбранные администраторы скрываются у остальных строк (связь 1:1). */
        availableCrmUsersFor(userId: string) {
            const taken = new Set(
                Object.entries(this.adminMapping)
                    .filter(([id, value]) => id !== userId && value)
                    .map(([, value]) => String(value))
            )
            return this.crmUsers
                .filter(user => !taken.has(String(user.id)))
                .map(user => ({
                    id: String(user.id),
                    label: user.email ? `${user.name} (${user.email})` : user.name,
                }))
        },
        async load() {
            this.loading = true
            this.error = false
            try {
                // список пользователей YClients тянется из внешнего API и может
                // отвечать долго — не держим на нём всю вкладку, грузим отдельно
                const [settings, admins] = await Promise.all([
                    api.gcb2.getOpz2Settings({ projectId: this.projectId }),
                    api.gcb2.getAdminsAndDHUsers({ projectId: this.projectId }),
                ])
                if (settings.error || !settings.data) {
                    this.error = true
                    return
                }
                this.scheme = {
                    name: settings.data.scheme.name ?? '',
                    appliesTo: settings.data.scheme.appliesTo ?? '',
                    description: settings.data.scheme.description ?? '',
                    actions: settings.data.scheme.actions ?? {},
                }
                this.actionsCatalog = settings.data.actionsCatalog ?? []
                this.accrueWhenOptions = settings.data.accrueWhenOptions ?? []
                this.serviceTypes = settings.data.serviceTypes ?? []
                this.staff = (settings.data.staff ?? []).map((row: any) => ({ ...row }))

                this.dhUsers = admins.data?.dhUsers ?? []
                this.adminMapping = (admins.data?.currentSettings ?? []).reduce(
                    (acc: Record<string, string | null>, item: any) => {
                        acc[item.id] = item.crmUserId ? String(item.crmUserId) : null
                        return acc
                    },
                    {}
                )
                this.loaded = true
                this.loadCrmUsers()
            } catch (err) {
                this.error = true
            } finally {
                this.loading = false
            }
        },
        async loadCrmUsers() {
            this.crmUsersLoading = true
            try {
                const res = await api.gcb2.getOpz2YcUsers({ projectId: this.projectId })
                // отдельный код бэкенда: интеграционному пользователю Data Heroes
                // в yClients не выдан доступ к сотрудникам — показываем, какие права
                // выдать, вместо общего «проверьте коннектор»
                this.crmUsersAccessDenied = res.error?.message === YC_USERS_NO_ACCESS
                this.crmUsers = res.data?.users ?? []
            } catch (err) {
                this.crmUsersAccessDenied = false
                this.crmUsers = []
            } finally {
                this.crmUsersLoading = false
            }
        },
        clearScheme() {
            this.scheme.name = ''
            this.scheme.appliesTo = ''
            this.scheme.description = ''
            Object.keys(this.scheme.actions).forEach(key => {
                this.$set(this.scheme.actions, key, {
                    enabled: false,
                    accrueWhen: 'onRecord',
                    rate: 0,
                    percent: 0,
                })
            })
            this.clearDialog = false
        },
        async save() {
            this.saving = true
            try {
                const [settingsRes, mappingRes] = await Promise.all([
                    api.gcb2.saveOpz2Settings({
                        projectId: this.projectId,
                        settings: {
                            name: this.scheme.name,
                            appliesTo: this.scheme.appliesTo,
                            description: this.scheme.description,
                            actions: this.scheme.actions,
                            staffServiceTypes: this.staff.map(row => ({
                                staffId: row.staffId,
                                serviceType: row.serviceType ?? null,
                            })),
                        },
                    }),
                    api.gcb2.saveAdminIdToUser({
                        settings: Object.entries(this.adminMapping).map(([userId, crmId]) => ({
                            userId,
                            crmId,
                        })),
                    }),
                ])
                if (settingsRes.error || mappingRes.error) {
                    this.$store.dispatch('callNotify', 'Не удалось сохранить настройки')
                    return
                }
                this.$store.dispatch('callNotify', 'Настройки сохранены')
                this.$emit('saved')
            } catch (err) {
                this.$store.dispatch('callNotify', 'Не удалось сохранить настройки')
            } finally {
                this.saving = false
            }
        },
    },
})
